import { useState } from 'react'

import { useFacilitySearch } from '@/hooks/useFacilitySearch'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import type { ColorScheme } from '@hivewatch/dark'
import { Flex } from '@hivewatch/dark'

import { SelectFilterField } from '@/components/filter-drawer'
import { FacilityOptionLabel } from '@/components/ui'
import { useStandardOperatingProceduresFilterDataQuery } from '@/graphql/generated/hooks'
import { FilterOptionI, createdAtOptions } from '@/utils/filterDrawers'
import {
  createFacilityAddressFilter,
  getIncidentFacilityOptionsWithAddress,
  getSOPCreatorOptions,
  getSOPTypeOptions,
} from '@/utils/forms/optionBuilders'
import {
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import type { SOPsFilterFormInputsI } from '../types/form'

interface DarkSOPsFilterFormIProps {
  control: Control<SOPsFilterFormInputsI>
  colorScheme: ColorScheme
}

export const DarkSOPsFilterForm = ({
  control,
  colorScheme,
}: DarkSOPsFilterFormIProps) => {
  const { t } = useTranslation()

  const [facilityName, setFacilityName] = useState('')
  const [debouncedFacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )

  const {
    facilitiesData,
    isFacilitiesLoading,
    isError: isFacilitiesError,
  } = useFacilitySearch({
    searchQuery: debouncedFacilityName,
  })

  const { data: sopFilterData } = useStandardOperatingProceduresFilterDataQuery(
    {
      fetchPolicy: 'cache-and-network',
    }
  )

  const facilityOptions = getIncidentFacilityOptionsWithAddress(
    facilitiesData,
    true,
    debouncedFacilityName
  )
  const creatorOptions = getSOPCreatorOptions(sopFilterData)
  // SopType is a string enum, so the option values key cleanly by String(value).
  const typeOptions = getSOPTypeOptions() as FilterOptionI<string>[]

  return (
    <Flex direction='column' gap='16px'>
      <SelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='sopsPage_filterDrawer_facilityName'
        filterOption={createFacilityAddressFilter}
        formatOptionLabel={(option, meta) => (
          <FacilityOptionLabel
            colorScheme={colorScheme}
            isValueContext={meta?.context === 'value'}
            option={option}
          />
        )}
        isLoading={isFacilitiesLoading}
        label={t('common:facility')}
        name='facilityId'
        noOptionsMessage={({ inputValue }) =>
          getFacilityNoOptionsMessage(
            inputValue,
            isFacilitiesError,
            t('common:facility_search_error')
          )
        }
        onInputChange={setFacilityName}
        options={facilityOptions}
        placeholder={t('sops:table_filter_facility_name_placeholder')}
      />
      <SelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='sopsPage_filterDrawer_createdAt'
        getOptionKey={(option) => option.label}
        isSearchable={false}
        label={t('common:created_at')}
        name='createdAt'
        options={createdAtOptions}
        placeholder={t('sops:table_filter_created_at_placeholder')}
      />
      <SelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='sopsPage_filterDrawer_creator'
        isSearchable={false}
        label={t('sops:table_filter_creator_label')}
        name='creator'
        options={creatorOptions}
        placeholder={t('sops:table_filter_creator_placeholder')}
      />
      <SelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='sopsPage_filterDrawer_type'
        isSearchable={false}
        label={t('sops:table_filter_operator_guard_label')}
        name='type'
        options={typeOptions}
        placeholder={t('sops:table_filter_operator_guard_placeholder')}
      />
    </Flex>
  )
}
